import Vue from "vue";
import Router from "vue-router";
import GetEventTrackingSettings from "./api/GetEventTrackingSettings";

import createYandexMetrikaScript from "./trackers/yandex.metrika";
import createGoogleAnalyticsScript from "./trackers/google.analytics";
import createFacebookPixelScript from "./trackers/facebook.pixel";
import isDisabledEnity from "./api/isDisabledEnity";
import hasNoAgreement from "./api/hasNoAgreement";
import checkOrder from "./api/checkOrder";

Vue.use(Router);

function createTrackers(s) {
    const $head = document.head;

    const $yandexMetrikaScript = createYandexMetrikaScript(s.yandexMetrikaID);
    const $googleAnalyticsScript = createGoogleAnalyticsScript(s.googleAnalyticsID);
    const $facebookPixelScript = createFacebookPixelScript(s.facebookPixelID);

    $head.appendChild($yandexMetrikaScript);
    $head.appendChild($googleAnalyticsScript);
    $head.appendChild($facebookPixelScript);
}

const router = new Router({
    mode: "history",
    base: process.env.BASE_URL,
    routes: [
        {
            path: "/payment/success",
            name: "payment.success",
            component: () => import(/* webpackChunkName: 'payment-success' */ "./views/payment/Success.vue"),
            async beforeEnter(to, from, next) {
                if (window.localStorage.getItem("@public/trackers")) {
                    createTrackers(JSON.parse(window.localStorage.getItem("@public/trackers")));
                }

                next();
            },
        },
        {
            path: "/payment/failed",
            name: "payment.failed",
            component: () => import(/* webpackChunkName: 'payment-failed' */ "./views/payment/Failed.vue"),
            async beforeEnter(to, from, next) {
                if (window.localStorage.getItem("@public/trackers")) {
                    createTrackers(JSON.parse(window.localStorage.getItem("@public/trackers")));
                }

                next();
            },
        },
        {
            path: "/events/:id",
            name: "events",
            component: () => import(/* webpackChunkName: 'event' */ "./views/Event.vue"),
            async beforeEnter(to, from, next) {
                if (await isDisabledEnity("events", to.params.id)) {
                    return next("/event/blocked");
                }

                if (await hasNoAgreement("events", to.params.id)) {
                    return next("/event/payment_off");
                }

                if (window.localStorage.getItem("@public/trackers")) {
                    window.localStorage.removeItem("@public/trackers");
                }

                const s = await GetEventTrackingSettings(to.params.id);

                if (Object.keys(s).length === 0) return next();

                window.localStorage.setItem("@public/trackers", JSON.stringify(s));

                createTrackers(s);

                next();
            },
        },
        {
            path: "/pay/:id",
            name: "pay",
            component: () => import(/* webpackChunkName: 'event' */ "./views/Order.vue"),
            async beforeEnter(to, from, next) {
                const check = await checkOrder(to.params.id);

                if (!check) {
                    return next("/notfound");
                }

                if (check.paid) {
                    return next("/paid?returning_uri=" + check.returning_uri);
                }

                if (window.localStorage.getItem("@public/trackers")) {
                    window.localStorage.removeItem("@public/trackers");
                }

                next();
            },
        },
        {
            path: "/paid",
            name: "paid",
            component: () => import(/* webpackChunkName: 'event' */ "./views/OrderPaid.vue"),
            async beforeEnter(to, from, next) {
                next();
            },
        },
        {
            path: "/collections/:id",
            name: "collections",
            component: () => import(/* webpackChunkName: 'collection' */ "./views/Collection.vue"),
            async beforeEnter(to, from, next) {
                if (await isDisabledEnity("collections", to.params.id)) {
                    return next("/event/blocked");
                }

                if (await hasNoAgreement("collections", to.params.id)) {
                    return next("/event/payment_off");
                }

                if (window.localStorage.getItem("@public/trackers")) {
                    window.localStorage.removeItem("@public/trackers");
                }

                next();
            },
        },
        {
            path: "/notifications/payment/success",
            name: "notification.payment.success",
            component: () => import(/* webpackChunkName: 'notification.payment.success' */ "./views/Notifications/Payment/Success.vue"),
            async beforeEnter(to, from, next) {
                if (window.localStorage.getItem("@public/trackers")) {
                    createTrackers(JSON.parse(window.localStorage.getItem("@public/trackers")));
                }

                next();
            },
        },
        {
            path: "/notifications/payment/success_order",
            name: "payment.success_order",
            component: () => import(/* webpackChunkName: 'payment-success' */ "./views/Notifications/Payment/SuccessOrder.vue"),
            async beforeEnter(to, from, next) {
                if (window.localStorage.getItem("@public/trackers")) {
                    createTrackers(JSON.parse(window.localStorage.getItem("@public/trackers")));
                }

                next();
            },
        },
        {
            path: "/notifications/payment/failed",
            name: "notification.payment.failed",
            component: () => import(/* webpackChunkName: 'notification.payment.failed' */ "./views/Notifications/Payment/Failed.vue"),
            async beforeEnter(to, from, next) {
                if (window.localStorage.getItem("@public/trackers")) {
                    createTrackers(JSON.parse(window.localStorage.getItem("@public/trackers")));
                }

                next();
            },
        },
        {
            path: "/event/blocked",
            name: "EventBlocked",
            component: () => import(/* webpackChunkName: 'event.blocked' */ "./views/Event/Blocked.vue"),
        },
        {
            path: "/event/payment_off",
            name: "PaymentOff",
            component: () => import(/* webpackChunkName: 'event.blocked' */ "./views/Event/PaymentOff.vue"),
        },
        {
            path: "*",
            name: "404",
            component: () => import(/* webpackChunkName: '404' */ "./views/404.vue"),
        },
    ],
});

const whiteList = ["notification.payment.success", "payment.success", "payment.success_order"];

router.beforeEach(async ({ name, query }, from, next) => {
    if (whiteList.includes(name)) {
        if (query.entity_id) {
            let s = {};

            switch (query.type) {
                case "events":
                    s = await GetEventTrackingSettings(query.entity_id);
                    break;
                case "collections":
                    break;
                case "order":
                    break;
                default:
                    break;
            }

            if (Object.keys(s).length !== 0) createTrackers(s);
        }
    }

    next();
});

export default router;
